import React, { useEffect, useState } from 'react';
import { Box, Chip, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { ITableHeadings, ITableMetadata } from '../../../common/interfaces/common.interface';
import BasicTable from '../../../components/BasicTable/BasicTable';
import { initialMetadataState, MESSAGE_TYPE } from '../../../common/constants/common.constants';
import TableHeader from '../../../components/TableHeader';
import { ChildDto, ImportDto } from '../../../common/interfaces/kindergarten.interface';
import { ReactComponent as AddIcon } from '../../../assets/add-icon.svg';
import { fetchChildren, getGroupsWithLocationName, updateChildStatus } from '../../../api/kindergarten.api';
import { createNotification } from '../../../utilities/helper/notification.helper';
import GenericStatusSwitch from '../../../components/GenericStatusSwitch';
import GenericButton, { GenericButtonTypeEnum } from '../../../components/GenericButton';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as EditIcon } from '../../../assets/edit-icon.svg';
import { ReactComponent as FaceIcon } from '../../../assets/face-icon.svg';
import { ReactComponent as ListEmptyStateIcon } from '../../../assets/list-empty-state-icon.svg';
import { FeatureCode, GeneralStatusEnum, ImportUserType, SortOrderEnum } from '../../../common/enums/common.enum';
import { useKindergartenFeatures } from '../../../utilities/hooks/useKindergartenFeatures';
import GenericInfoModal from '../../../components/GenericInfoModal';
import ToggleWithLabel from '../../../components/ToggleWithLabel';
import { ChildrenTabSortKeyEnum } from '../../../common/enums/kindergarten.enum';
import GenericMultiSelectWithSearch from '../../../components/GenericMultiSelectWithSearch';
import GenericImportButton from '../../../components/GenericImportButton';
import { formatMoney } from '../../../utilities/helper/money.helper';
import { ChildBalanceDto } from '../../../common/interfaces/invoicing.interface';
import { getBalances } from '../../../api/invoicing.api';
import { FINANCE_DEBTS } from '../../../common/constants/routes.constants';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../theme';
export const CHILDREN_TABLE_HEADINGS: ITableHeadings[] = [
  { id: 1, value: '', label: 'common.name', sortable: true, sortKey: ChildrenTabSortKeyEnum.NAME, width: '20%' },
  { id: 2, value: '', label: 'children.birthday', sortable: true, sortKey: ChildrenTabSortKeyEnum.BIRTHDAY },
  { id: 3, value: '', label: 'kindergarten.tabs.parents', sortable: true, sortKey: ChildrenTabSortKeyEnum.PARENTS, width: '25%' },
  { id: 4, value: '', label: 'common.groups', sortable: true, sortKey: ChildrenTabSortKeyEnum.GROUPS },
  { id: 5, value: '', label: 'common.status', sortable: true, sortKey: ChildrenTabSortKeyEnum.STATUS },
  { id: 6, value: '', label: '', sortable: false }
];

/** The same list with what each child owes, shown when the billing toggle is on. */
const CHILDREN_TABLE_HEADINGS_WITH_BILLING: ITableHeadings[] = [
  ...CHILDREN_TABLE_HEADINGS.slice(0, 5),
  { id: 7, value: '', label: 'finance.figures.outstanding', sortable: false },
  CHILDREN_TABLE_HEADINGS[5]
];

const ChildrenTab: React.FC<{ kindergartenId: string; kindergartenName: string }> = ({ kindergartenId, kindergartenName }) => {
  const { t } = useTranslation();
  const [children, setChildren] = useState<ChildDto[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<ITableMetadata>(initialMetadataState);
  const [withParents, setWithParents] = useState<boolean>(false);
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useState<string>('');
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<ChildrenTabSortKeyEnum>();
  const [sortOrder, setSortOrder] = useState<SortOrderEnum>();
  const [childToDeactivate, setChildToDeactivate] = useState<ChildDto | null>(null);
  const [importWarning, setImportWarning] = useState<ImportDto[]>();
  const [withBilling, setWithBilling] = useState<boolean>(false);
  const { isEnabled } = useKindergartenFeatures(kindergartenId);
  const isInvoicingEnabled = isEnabled(FeatureCode.INVOICING);
  // Keyed by child id. Fetched for the whole kindergarten in one call rather than per
  // page, so paging through the list does not refetch and the totals below stay stable.
  const [balances, setBalances] = useState<Record<string, ChildBalanceDto>>({});
  const [pageOutstanding, setPageOutstanding] = useState(0);

  useEffect(() => {
    loadGroups();
  }, [kindergartenId]);

  useEffect(() => {
    loadChildren();
  }, [kindergartenId, metadata.currentPage, metadata.itemsPerPage, searchParams, selectedGroupIds, sortBy, sortOrder, withParents]);

  const handleSearchChange = (newSearch: string) => {
    setMetadata(prev => ({ ...prev, currentPage: 1 }));
    setSearchParams(newSearch);
  };

  const handleSortChange = (sortKey: string | null, order: 'asc' | 'desc' | null) => {
    if (sortKey === 'parents' && !withParents) return;
    setSortBy(sortKey ? (sortKey as ChildrenTabSortKeyEnum) : undefined);
    setSortOrder(order ? (order as SortOrderEnum) : undefined);
  };

  const loadChildren = async () => {
    setLoader(true);
    try {
      const result = await fetchChildren(
        kindergartenId,
        metadata.currentPage,
        metadata.itemsPerPage,
        sortBy,
        sortOrder,
        searchParams,
        selectedGroupIds,
        withParents
      );
      setChildren(result.results ?? []);
      if (result.paginationInfo) {
        setMetadata(prev => ({
          ...prev,
          totalItems: result.paginationInfo?.totalItems ?? 0,
          totalPages: result.paginationInfo?.totalPages ?? 0
        }));
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('children.loadFailed'));
    } finally {
      setLoader(false);
    }
  };

  /**
   * Loaded only when asked for, and only for the children on screen: most visits to
   * this list are not about money, and invoicing may be switched off for the
   * kindergarten entirely - in which case the call 403s and the column stays empty
   * rather than showing an error.
   */
  useEffect(() => {
    if (!withBilling || !kindergartenId || !children.length) return;
    // naming the children opts out of paging server-side, so perPage is not sent:
    // it is capped at 100 by validation and would only be ignored anyway
    const childIds = children.map(child => child.id);
    getBalances(kindergartenId, { childIds })
      .then(result => {
        const byChild: Record<string, ChildBalanceDto> = {};
        for (const balance of result.results?.items ?? []) byChild[balance.childId] = balance;
        setBalances(byChild);
        setPageOutstanding(result.results?.totalOutstanding ?? 0);
      })
      .catch(() => setBalances({}));
  }, [withBilling, kindergartenId, children]);

  const debtorsOnPage = Object.values(balances).filter(balance => balance.outstanding > 0);

  const loadGroups = async () => {
    try {
      const response = await getGroupsWithLocationName(kindergartenId);
      if (response?.results) {
        setGroups(response.results);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('groups.listLoadFailed'));
    }
  };

  const handleChangeChildStatus = async (childId: string) => {
    if (!kindergartenId) return;

    try {
      await updateChildStatus(kindergartenId, childId);
      createNotification(MESSAGE_TYPE.SUCCESS, t('children.statusChanged'));
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, t('children.statusChangeFailed'));
    }
  };

  const handleConfirmDeactivation = async () => {
    if (!childToDeactivate) return;
    handleChangeChildStatus(childToDeactivate.id);
    setChildren(prev =>
      prev.map(t => (t.id === childToDeactivate.id ? { ...t, status: GeneralStatusEnum.INACTIVE, group: undefined } : t))
    );
    setChildToDeactivate(null);
  };

  const handleCreateClick = () => {
    navigate(`/${kindergartenId}/child/create/${encodeURIComponent(kindergartenName)}`);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: 24,
            color: 'text.primary',
            fontFamily: 'Poppins'
          }}
        >
          {t('kindergarten.tabs.children')}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <GenericImportButton
            label={t('common.import')}
            type={ImportUserType.CHILDREN}
            kindergartenId={kindergartenId}
            onSuccessImport={loadChildren}
          />
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            label={t('common.create')}
          />
        </Stack>
      </Stack>
      <Box
        sx={{
          border: `1px solid ${palette.line}`,
          borderRadius: 4
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}
        >
          <TableHeader searchTitle={t('common.searchByName')} sortOptions={[]} setSearchValue={handleSearchChange} />
          <GenericMultiSelectWithSearch
            label={t('common.group')}
            options={groups}
            onClearAll={async () => {
              setSelectedGroupIds([]);
            }}
            onShowResults={async ids => {
              setSelectedGroupIds(ids);
              setMetadata(prev => ({ ...prev, currentPage: 1 }));
            }}
          />
          <ToggleWithLabel label={t('children.withParents')} checked={withParents} onChange={setWithParents} />
          {/* offered only where there is billing to show; otherwise it turns on and
              stays empty, which reads as a bug rather than as a feature being off */}
          {isInvoicingEnabled && <ToggleWithLabel label={t('children.withBilling')} checked={withBilling} onChange={setWithBilling} />}
        </Box>

        {withBilling && (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 2, pb: 1 }}>
            <Typography sx={{ fontSize: 14 }}>
              {/* scoped to this page, and said so: the list pages on the server, so a
                  kindergarten-wide total here would not match the rows shown */}
              {debtorsOnPage.length
                ? t('children.debtorsOnPage', {
                    count: debtorsOnPage.length,
                    total: formatMoney(pageOutstanding, { withCurrency: true })
                  })
                : t('children.noDebtorsOnPage')}
            </Typography>
            {/* filtering to debtors belongs on the Debts screen, where the backend
                pages it; filtering here would return short pages */}
            {!!debtorsOnPage.length && (
              <GenericButton
                buttonType={GenericButtonTypeEnum.Neutral}
                variant="outlined"
                label={t('children.openDebts')}
                onClick={() => navigate(`${FINANCE_DEBTS}?kindergartenId=${kindergartenId}`)}
              />
            )}
          </Stack>
        )}

        <BasicTable
          headings={withBilling ? CHILDREN_TABLE_HEADINGS_WITH_BILLING : CHILDREN_TABLE_HEADINGS}
          loader={loader}
          metadata={metadata}
          setMetadata={setMetadata}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        >
          {children.map(child => (
            <TableRow
              key={child.id}
              hover
              sx={{ cursor: ' pointer' }}
              onClick={() => navigate(`/${kindergartenId}/child/${child.id}/details/${encodeURIComponent(kindergartenName)}`)}
            >
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  {child.profileImage ? (
                    <img
                      src={child.profileImage}
                      alt={`${child.firstName} ${child.middleName} ${child.lastName}`}
                      style={{ color: 'text.primary', width: 34, height: 34, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    <FaceIcon style={{ width: 34, height: 34 }} />
                  )}
                  <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 400 }}>
                    {`${child.firstName} (${child.middleName}) ${child.lastName}`}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>{child.birtDate?.split('T')[0]}</TableCell>
              <TableCell>{child.parents?.map((g: any) => g.fullName).join(', ') ?? '-'}</TableCell>
              <TableCell>{child.group ? child.group.name : '-'}</TableCell>
              <TableCell>
                <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                  <GenericStatusSwitch
                    status={child.status}
                    onChange={() => {
                      if (child.status === GeneralStatusEnum.ACTIVE) {
                        setChildToDeactivate(child);
                      } else {
                        handleChangeChildStatus(child.id);
                        setChildren(prev =>
                          prev.map(t =>
                            t.id === child.id
                              ? {
                                  ...t,
                                  status: t.status === GeneralStatusEnum.ACTIVE ? GeneralStatusEnum.INACTIVE : GeneralStatusEnum.ACTIVE
                                }
                              : t
                          )
                        );
                      }
                    }}
                    showStatusText={false}
                  />
                </Box>
              </TableCell>
              {withBilling && (
                <TableCell>
                  {balances[child.id] ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography sx={{ fontSize: 14, fontWeight: balances[child.id].outstanding > 0 ? 700 : 400 }}>
                        {formatMoney(balances[child.id].outstanding, { withCurrency: true })}
                      </Typography>
                      {balances[child.id].overdueCount > 0 && (
                        <Chip
                          size="small"
                          color="error"
                          variant="outlined"
                          label={t('billing.overdue', { count: balances[child.id].overdueCount })}
                        />
                      )}
                      {balances[child.id].credit > 0 && (
                        <Chip
                          size="small"
                          color="info"
                          variant="outlined"
                          label={t('billing.creditAmount', { amount: formatMoney(balances[child.id].credit) })}
                        />
                      )}
                    </Stack>
                  ) : (
                    '—'
                  )}
                </TableCell>
              )}
              <TableCell>
                <EditIcon
                  style={{ cursor: 'pointer' }}
                  onClick={e => {
                    e.stopPropagation();
                    navigate(`/${kindergartenId}/child/${child.id}/edit/${encodeURIComponent(kindergartenName)}`);
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
          {childToDeactivate && (
            <GenericInfoModal
              open={true}
              onClose={() => setChildToDeactivate(null)}
              onConfirm={handleConfirmDeactivation}
              label={t('children.deactivate')}
              message={t('common.confirmDeactivate')}
              confirmLabel={t('common.deactivate')}
              entityName={`${childToDeactivate.firstName} (${childToDeactivate.middleName}) ${childToDeactivate.lastName}`}
            />
          )}
        </BasicTable>
      </Box>
    </Box>
  );
};
export default ChildrenTab;
